"use client";

import { signInWithTwitter } from '@/lib/authActions';
import React, { useState, useEffect } from "react";
import { FaXTwitter } from "react-icons/fa6";
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

type TwitterSignInResult = 
  | { error: string }
  | { url: string };

const SignInWithTwitterButton = () => {
  const t = useTranslations('login.signInWithTwitter');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  
  // Check for auth success cookie on component mount and refresh
  useEffect(() => {
    const checkAuthSuccess = () => {
      const authSuccess = document.cookie.includes('auth_success=true');
      if (authSuccess) {
        // Clear the cookie and refresh the page
        document.cookie = "auth_success=; path=/; max-age=0";
        router.refresh();
      }
    };
    
    checkAuthSuccess(); // Check on mount
    
    // Also listen for focus events (when user comes back to the tab)
    window.addEventListener('focus', checkAuthSuccess);
    return () => window.removeEventListener('focus', checkAuthSuccess);
  }, [router]);
  
  const handleTwitterSignIn = async () => {
    try {
      setIsLoading(true); 
      setError(null);
      const result = await signInWithTwitter() as TwitterSignInResult;
      
      if ('error' in result) {
        const errorMessage = result.error;
        console.error("Twitter sign-in returned error:", errorMessage);
        
        // Provide more user-friendly error messages
        if (errorMessage.includes("popup")) {
          setError("Pop-up blocked by browser. Please allow pop-ups and try again.");
        } else if (errorMessage.includes("network") || errorMessage.includes("connection")) {
          setError("Network error. Please check your connection and try again.");
        } else {
          setError(errorMessage);
        }
        return;
      }
      
      if (result && 'url' in result) {
        // Client-side redirect to Twitter's OAuth page
        window.location.href = result.url;
      } else {
        setError("Authentication failed: Invalid response");
      }
    } catch (error) {
      console.error("Twitter sign-in error:", error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="w-full">
      <button
        type="button"
        className="w-full flex items-center py-3 px-4 rounded-full bg-static-white text-static-black font-medium hover:bg-gray-100 transition-all border border-text-main/20 shadow-sm"
        onClick={handleTwitterSignIn}
        disabled={isLoading}
      >
        <FaXTwitter className="w-6 h-6 text-static-black" />
        <span className="flex-grow text-center font-bold text-static-black">
          {isLoading ? t('loading') : t('button')}
        </span>
      </button>
      {error && (
        <p className="mt-2 text-sm text-status-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default SignInWithTwitterButton; 